/**
 * Chat (see the worker's household/chat.ts): Terrazzo keeps the conversations and a helper
 * on someone's Mac runs each message with Claude Code. This keeps one chat's messages
 * fresh while it's open (quickly while a reply is coming, slowly otherwise), and watches for
 * the reply to something sent from elsewhere (the command box) so it can say so.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react'
import { showToast } from '../store/toasts'
import { navigate } from './router'
import { actions } from '../store/actions'
import { store } from '../store/store'
import { myId } from '../store/me'
import { shortId, ulid } from '@terrazzo/core'

export type ChatKey = 'shared' | 'mine'

export interface Question {
  question: string
  options: string[]
  multi?: boolean
}

export interface ChatMessage {
  id: string
  chat: string
  thread: string
  author: string
  role: 'user' | 'claude' | 'note'
  text: string
  data?: {
    questions?: Question[]
    answered?: string[]
    changes?: Array<{ id: string; title: string; op: string }>
    batch?: string
    reverted?: boolean
    error?: string
    command?: 'compact'
    /** A "/" message passed on to Claude Code (a skill or command). */
    slash?: string
    /** What the assistant did on the way to its reply. */
    steps?: Step[]
    /** Photos sent with the message. */
    files?: ChatFile[]
  }
  status?: string
  replyTo?: string
  at: number
  updated: number
}

/** A photo sent with a message (stored like an item's, under att/chat-…). */
export interface ChatFile {
  key: string
  thumbKey?: string
  mime: string
  w?: number
  h?: number
  name?: string
}

/** A tool it used (name and its input, as JSON), or something it said along the way. */
export type Step = { tool: string; input?: string; error?: boolean } | { said: string }

/** A skill on the Mac that "/" runs. */
export interface Skill {
  name: string
  description?: string
}

export interface RunnerInfo {
  id: string
  name: string
  owner: string
  ownerId: string
  online: boolean
  paused: boolean
  busy: boolean
  lastSeen: number | null
  /** Something on the Mac needs a person (e.g. Claude Code signed out). */
  problem?: string
}

export interface ChatSettings {
  instructions: string
  model: string
  freshHours: number
  askBefore: number
}

export interface ChatState {
  chat: string
  chats: { shared: boolean; mine: string }
  settings: ChatSettings
  messages: ChatMessage[]
  more: boolean
  threads: Array<{ id: string; startedAt: number; closed: boolean; summary?: string }>
  runner: RunnerInfo | null
  /** Skills "/" can run on that Mac. */
  skills: Skill[]
  mine: RunnerInfo[]
  sharing: boolean
  sharer: string | null
  now: number
}

export class ChatApiError extends Error {}

export async function chatApi<T>(path: string, init?: { method?: string; json?: unknown }): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method: init?.method ?? (init?.json !== undefined ? 'POST' : 'GET'),
    credentials: 'same-origin',
    headers: init?.json !== undefined ? { 'content-type': 'application/json' } : undefined,
    body: init?.json !== undefined ? JSON.stringify(init.json) : undefined,
  })
  if (!res.ok) {
    let msg = `Couldn't reach Terrazzo (${res.status})`
    try {
      msg = ((await res.json()) as { error?: string }).error ?? msg
    } catch {
      // keep the generic message
    }
    throw new ChatApiError(msg)
  }
  return (await res.json()) as T
}

const keyOf = (chat: ChatKey) => (chat === 'shared' ? 'shared' : 'mine')

/** The chat to send to from elsewhere: the last one you wrote in (it falls back to yours). */
export function lastChat(): ChatKey {
  return store.userSettings(myId()).chatLast === 'mine' ? 'mine' : 'shared'
}

export function rememberChat(chat: ChatKey) {
  if (store.userSettings(myId()).chatLast !== chat) actions.setMine('chatLast', chat)
}

export async function sendMessage(chat: ChatKey, text: string, files: ChatFile[] = []): Promise<{ message?: ChatMessage; note?: string }> {
  const r = await chatApi<{ message?: ChatMessage; note?: string }>('/chat/send', { json: { chat: keyOf(chat), text, ...(files.length ? { files } : {}) } })
  rememberChat(chat)
  return r
}

// ---------------------------------------------------------------------------
// One chat, kept fresh while it's on screen

const merge = (list: ChatMessage[], more: ChatMessage[]) => {
  const byId = new Map(list.map((m) => [m.id, m]))
  for (const m of more) byId.set(m.id, m)
  return [...byId.values()].sort((a, b) => a.at - b.at)
}

/** The last state of each chat this session, so switching back shows it at once (then catches up). */
const seen = new Map<ChatKey, ChatState>()

export function useChat(chat: ChatKey) {
  const [state, setState] = useState<ChatState | null>(() => seen.get(chat) ?? null)
  const [error, setError] = useState<string | null>(null)
  const latest = useRef<ChatState | null>(null)
  latest.current = state
  const busy = !!state?.messages.some((m) => m.status === 'queued' || m.status === 'running')

  const load = useCallback(async () => {
    try {
      const prev = latest.current
      const since = prev && prev.chat !== '' ? Math.max(0, ...prev.messages.map((m) => m.updated)) : 0
      const fresh = await chatApi<ChatState>(`/chat?c=${keyOf(chat)}${prev && since ? `&since=${since}` : ''}`)
      setError(null)
      setState((cur) => {
        let next = fresh
        if (cur && since && cur.chat === fresh.chat) {
          const threads = new Map(cur.threads.map((t) => [t.id, t]))
          for (const t of fresh.threads) threads.set(t.id, t)
          next = { ...fresh, messages: merge(cur.messages, fresh.messages), more: cur.more, threads: [...threads.values()] }
        }
        seen.set(chat, next)
        return next
      })
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }, [chat])

  useLayoutEffect(() => {
    const cached = seen.get(chat) ?? null
    setState(cached)
    latest.current = cached
  }, [chat])

  // Quickly while something is waiting or being answered, slowly otherwise; the pace switches
  // the moment that changes (sending restarts it at the quick pace).
  useEffect(() => {
    void load()
    let t: ReturnType<typeof setTimeout>
    let stopped = false
    const tick = () => {
      t = setTimeout(
        async () => {
          if (document.visibilityState === 'visible') await load()
          if (!stopped) tick()
        },
        busy ? 1200 : 8000,
      )
    }
    tick()
    const onVisible = () => document.visibilityState === 'visible' && void load()
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      stopped = true
      clearTimeout(t)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [load, busy])

  const loadEarlier = useCallback(async () => {
    const cur = latest.current
    if (!cur?.messages.length) return
    const older = await chatApi<ChatState>(`/chat?c=${keyOf(chat)}&before=${cur.messages[0]!.at}`)
    setState((s) => (s ? { ...s, messages: merge(older.messages, s.messages), more: older.more, threads: [...older.threads, ...s.threads] } : s))
  }, [chat])

  return { state, error, busy, reload: load, loadEarlier, setState }
}

// ---------------------------------------------------------------------------
// Sent from elsewhere: say when the answer comes back

const watching = new Set<string>()
const subs = new Set<() => void>()
let onChatPage = false
export function setOnChatPage(v: boolean) {
  onChatPage = v
}

/** Waits for the reply to a message sent from outside the Chat page, then shows it in a toast. */
export function watchReply(chat: ChatKey, messageId: string) {
  if (watching.has(messageId)) return
  watching.add(messageId)
  for (const f of subs) f()
  const started = Date.now()
  let since = Date.now() - 5000
  const poll = async () => {
    if (Date.now() - started > 15 * 60_000) return watching.delete(messageId)
    try {
      const s = await chatApi<ChatState>(`/chat?c=${keyOf(chat)}&since=${since}`)
      since = Math.max(since, ...s.messages.map((m) => m.updated))
      const reply = s.messages.find((m) => m.replyTo === messageId && m.role === 'claude')
      if (reply && (reply.status === 'done' || reply.status === 'error')) {
        watching.delete(messageId)
        for (const f of subs) f()
        if (onChatPage) return
        const q = reply.data?.questions?.[0]?.question
        const text = reply.status === 'error' ? `Couldn't finish: ${reply.data?.error ?? 'something went wrong'}` : q ? q : reply.text || 'Done'
        showToast(text.length > 140 ? text.slice(0, 139) + '…' : text, { action: { label: 'Open', run: () => navigate(`/chat?c=${chat}`) }, timeout: 12000 })
        return
      }
    } catch {
      // try again
    }
    setTimeout(poll, 2500)
  }
  setTimeout(poll, 1500)
}

export function usePendingReplies(): number {
  return useSyncExternalStore(
    (cb) => (subs.add(cb), () => subs.delete(cb)),
    () => watching.size,
  )
}

/** Send from the command box (or anywhere but the Chat page): toast now, and again with the answer. */
export async function askInChat(text: string): Promise<void> {
  const chat = lastChat()
  try {
    const r = await sendMessage(chat, text)
    if (r.message?.status === 'queued') {
      showToast('Sent to Chat', { action: { label: 'Open', run: () => navigate(`/chat?c=${chat}`) } })
      watchReply(chat, r.message.id)
    } else if (r.note) showToast(r.note)
  } catch (e) {
    // The Shared chat went away (nobody shares now): try your own.
    if (chat === 'shared' && e instanceof ChatApiError && /sharing/i.test(e.message)) return void askIn('mine', text)
    showToast(e instanceof Error ? e.message : String(e))
  }
}

async function askIn(chat: ChatKey, text: string) {
  try {
    const r = await sendMessage(chat, text)
    if (r.message) {
      showToast('Sent to Chat', { action: { label: 'Open', run: () => navigate(`/chat?c=${chat}`) } })
      watchReply(chat, r.message.id)
    }
  } catch (e) {
    showToast(e instanceof Error ? e.message : String(e))
  }
}

/** Terrazzo's own slash commands (handled here, not sent on); any other "/" goes to Claude Code as typed. */
export const SLASH_COMMANDS: Array<{ name: string; args?: string; hint: string }> = [
  { name: 'new', hint: 'Start a new conversation' },
  { name: 'compact', hint: 'Summarize, then carry on in a new conversation' },
  { name: 'model', args: 'opus · sonnet · haiku · default', hint: 'Which model answers here' },
  { name: 'instructions', hint: 'Standing context for every conversation' },
]

// ---------------------------------------------------------------------------
// Photos for a message: resized like an item's (2000 px, with a small copy) and uploaded
// before it's sent, so the assistant can look at them (terrazzo_file).

/** Most photos one message may carry (the server keeps no more). */
export const MAX_PHOTOS = 6

export async function uploadChatPhoto(file: File): Promise<ChatFile> {
  const { processImage } = await import('./uploads')
  // As much detail as Claude takes in (1568 px), and lighter to send.
  const { full, thumb } = await processImage(file, 1568)
  const folder = `chat-${ulid()}`
  const id = shortId()
  const put = async (blob: Blob, variant: 'full' | 'thumb') => {
    const res = await fetch(`/api/files/${folder}/${id}?variant=${variant}`, { method: 'PUT', credentials: 'same-origin', headers: { 'content-type': blob.type }, body: blob })
    if (!res.ok) throw new ChatApiError(`Couldn't upload ${file.name || 'the photo'} (${res.status})`)
    return ((await res.json()) as { key: string }).key
  }
  const [key, thumbKey] = await Promise.all([put(full.blob, 'full'), put(thumb.blob, 'thumb')])
  return { key, thumbKey, mime: full.blob.type, w: full.w, h: full.h, ...(file.name ? { name: file.name } : {}) }
}
